import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import {
  useGetNotificationSettingsQuery,
  useGetUserEventTypesQuery,
  useUpdateNotificationSettingsMutation,
} from "store/api/notificationApi"

import getErrorMessage from "utils/getErrorMessage"
import {
  hasActiveMonthlyEngagementUserEventType,
  MONTHLY_ENGAGEMENT_REPORT_EVENT_TYPE,
} from "utils/notificationEventTypes"
import { Spinner } from "components/common/Spinner"

import { NotificationSettingRow } from "./NotificationSettingRow"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px 20px;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const TableHeader = styled.div`
  display: flex;
  background-color: var(--surface-3);
  width: 100%;
  margin-top: 20px;
`

const HeaderCell = styled.div<{ width?: string; align?: string }>`
  flex: ${({ width }) => (width ? `0 0 ${width}` : "1 0 0")};
  padding: 8px 20px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.48px;
  text-transform: uppercase;
  color: var(--text-muted);
  text-align: ${({ align }) => align || "left"};
`

interface NotificationSettingItem {
  event_type_id: number
  event_name: string
  email_enabled: boolean
  inapp_enabled: boolean
}

export const AccountSettingsNotifications = () => {
  const { data: userEventTypesData, isLoading: isLoadingEventTypes } = useGetUserEventTypesQuery()
  const { data: settingsData, isLoading: isLoadingSettings } = useGetNotificationSettingsQuery()
  const [updateSettings] = useUpdateNotificationSettingsMutation()

  const [notifications, setNotifications] = useState<NotificationSettingItem[]>([])
  const [savingCheckbox, setSavingCheckbox] = useState<string | null>(null)

  const combinedNotifications = useMemo(() => {
    if (!userEventTypesData?.data || !settingsData?.data) return []

    const canShowMonthlyEngagementReport = hasActiveMonthlyEngagementUserEventType(userEventTypesData.data)

    const activeEventTypes = userEventTypesData.data.filter(
      type => type.active && (canShowMonthlyEngagementReport || type.id !== MONTHLY_ENGAGEMENT_REPORT_EVENT_TYPE.id),
    )

    return activeEventTypes.map(eventType => {
      const setting = settingsData.data.find(s => s.event_type_id === eventType.id)
      return {
        event_type_id: eventType.id,
        event_name: eventType.name,
        email_enabled: setting?.email_enabled ?? true,
        inapp_enabled: setting?.inapp_enabled ?? true,
      }
    })
  }, [userEventTypesData, settingsData])

  useEffect(() => {
    if (combinedNotifications.length > 0) {
      setNotifications(combinedNotifications)
    }
  }, [combinedNotifications])

  const handleNotificationChange = async (eventTypeId: number, type: "email" | "inapp") => {
    const checkboxKey = `${type}-${eventTypeId}`
    setSavingCheckbox(checkboxKey)

    const updatedNotifications = notifications.map(notification =>
      notification.event_type_id === eventTypeId
        ? {
            ...notification,
            [type === "email" ? "email_enabled" : "inapp_enabled"]: !(type === "email"
              ? notification.email_enabled
              : notification.inapp_enabled),
          }
        : notification,
    )
    setNotifications(updatedNotifications)

    try {
      const settingsToSave = updatedNotifications.map(notification => ({
        event_type_id: notification.event_type_id,
        email_enabled: notification.email_enabled,
        inapp_enabled: notification.inapp_enabled,
      }))

      const result = await updateSettings({ settings: settingsToSave })
      const error = getErrorMessage(result?.error)
      if (error) {
        toast.error(error)
        setNotifications(notifications)
      }
    } catch {
      toast.error("Failed to update notification settings")
      setNotifications(notifications)
    } finally {
      setSavingCheckbox(null)
    }
  }

  if (isLoadingEventTypes || isLoadingSettings) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </Container>
    )
  }

  return (
    <Container>
      <TableWrapper>
        <TableHeader>
          <HeaderCell>EVENTS</HeaderCell>
          <HeaderCell width="120px" align="right">
            Email
          </HeaderCell>
          <HeaderCell width="120px" align="right">
            In-app
          </HeaderCell>
        </TableHeader>
        {notifications.map(notification => (
          <NotificationSettingRow
            key={notification.event_type_id}
            event_type_id={notification.event_type_id}
            event_name={notification.event_name}
            email_enabled={notification.email_enabled}
            inapp_enabled={notification.inapp_enabled}
            onEmailChange={(eventTypeId: number) => handleNotificationChange(eventTypeId, "email")}
            onInAppChange={(eventTypeId: number) => handleNotificationChange(eventTypeId, "inapp")}
            savingCheckbox={savingCheckbox}
          />
        ))}
      </TableWrapper>
    </Container>
  )
}
